import { getFormMessageIdentity, synchronizeIssuedFormState, type FormStateMessage } from './form-message-state'

type IdentifiedMessage = { _id?: string; client_message_id?: string; type?: string }

/** A planned handoff keeps pending actions alive while canonical history arrives. */
export function mergeMigratedConversationHistory<T extends IdentifiedMessage & FormStateMessage>(
  messages: T[],
  history: T[]
): T[] {
  const cardIdentity = (message: T): string | undefined => {
    const payload = message.content
    if (!payload || typeof payload !== 'object') return undefined
    if (message.type === 'component' && 'component_id' in payload && payload.component_id === 'guest_info_request') {
      return 'guest_info_request'
    }
    if (message.type === 'article' && 'article_id' in payload && typeof payload.article_id === 'string') {
      const index = 'message_index' in payload ? payload.message_index : 0

      return `article:${payload.article_id}:${index}`
    }

    return undefined
  }

  let merged = messages
  for (const incoming of history) {
    const formIdentity = getFormMessageIdentity(incoming)
    const incomingCard = cardIdentity(incoming)
    const index = merged.findIndex(message =>
      Boolean(
        (incoming._id && message._id === incoming._id) ||
        (incoming.client_message_id && message.client_message_id === incoming.client_message_id) ||
        (formIdentity && getFormMessageIdentity(message) === formIdentity) ||
        (incomingCard && incomingCard === cardIdentity(message) && !message._id)
      )
    )
    if (index < 0) {
      merged = [...merged, incoming]
      continue
    }
    const existing = merged[index]
    const replacement =
      existing.type === 'file_placeholder'
        ? { ...incoming, ...existing, _id: incoming._id ?? existing._id }
        : { ...existing, ...incoming }
    merged = merged.map((message, i) => (i === index ? replacement : message))
  }

  return synchronizeIssuedFormState(merged)
}

export const sessionClosureMessageId = (sessionId?: string | null) => `session-closed:${sessionId ?? 'current'}`

/** First-page history replaces chat content, but the terminal notice is a live-only frame. */
export function replaceConversationHistory<T extends IdentifiedMessage>(
  messages: T[],
  history: T[],
  sessionId?: string | null
): T[] {
  const notice = messages.find(message => message._id === sessionClosureMessageId(sessionId))

  return notice ? mergeConversationMessage(history, notice) : history
}

/** Merge a server echo with an optimistic message, or a live frame with its history row. */
export function mergeConversationMessage<T extends IdentifiedMessage>(messages: T[], incoming: T): T[] {
  const index = messages.findIndex(
    message =>
      (incoming._id && message._id === incoming._id) ||
      (incoming.client_message_id && message.client_message_id === incoming.client_message_id)
  )
  if (index < 0) return [...messages, incoming]

  return messages.map((message, i) => {
    if (i !== index) return message
    // Preserve local file previews when the server confirms the associated user text.
    if (message.type === 'file_placeholder') return { ...incoming, ...message, _id: incoming._id ?? message._id }

    return { ...message, ...incoming }
  })
}
